// Latest-wins fetching for one resource: of the fetches that overlap, only
// the newest one's result is applied, and never one that a write has made
// stale. Stores give it the fetch and what to do with a result, and call
// `run` whenever the resource may have changed.

/** What a fetch produced. */
export type Outcome<T> = { ok: true; value: T } | { ok: false; error: unknown };

export class Latest<T> {
  private ticket = 0;
  private epoch = 0;
  /** Runs with a ticket up to this were discarded (see `discard`). */
  private dropped = 0;
  private disposed = false;

  constructor(
    private fetch: () => Promise<T>,
    /** Applies a current outcome; `ticket` numbers its run (see `started`). */
    private apply: (outcome: Outcome<T>, ticket: number) => void | Promise<void>,
  ) {}

  /** The ticket of the newest run started (0 before any): runs are numbered in the order they start. */
  get started(): number {
    return this.ticket;
  }

  /**
   * Fetch, and apply the outcome if it is still current: no later `run`
   * has started, no `invalidate` or `discard` happened while it was in
   * flight, and the resource is not disposed. A fetch that only an
   * `invalidate` made stale is run again, so the newest state lands
   * without waiting for another trigger. Resolves once the outcome is
   * applied or discarded.
   */
  async run(): Promise<void> {
    if (this.disposed) return;
    const ticket = ++this.ticket;
    const epoch = this.epoch;
    let outcome: Outcome<T>;
    try {
      outcome = { ok: true, value: await this.fetch() };
    } catch (error) {
      outcome = { ok: false, error };
    }
    if (this.disposed || ticket !== this.ticket || ticket <= this.dropped) return;
    if (epoch !== this.epoch) return this.run();
    await this.apply(outcome, ticket);
  }

  /** A write changed the resource: fetches in flight now would apply a stale result, so they run again. */
  invalidate(): void {
    this.epoch++;
  }

  /**
   * Like `invalidate`, but fetches in flight are only dropped, not run
   * again: for a writer that runs the resource itself once it is done.
   */
  discard(): void {
    this.dropped = this.ticket;
  }

  /** Apply nothing from now on. */
  dispose(): void {
    this.disposed = true;
  }
}
